import { IGeoLocation } from 'src/common-types/index';
import MapMarker from 'src/assets/images/map-price-icon.svg';
import MapMarkerSelected from 'src/assets/images/map-price-selected-icon.svg';
import MapHomeMarker from 'src/assets/images/map-home-icon.svg';
import MapHomeSelectedMarker from 'src/assets/images/max-home-selected-icon.svg';
import MapHomeDisabledMarker from 'src/assets/images/map-home-disabled.svg';
import { DEFAULT_MAP_ZOOM } from 'src/constants/index';

const zero = 0;
const two = 2;
const topZIndex = '5';
const noPriceIconWidth = '33px';
const MAX_SHOW_AREA = 4000;

export class PriceMarker extends google.maps.OverlayView {
  position: google.maps.LatLng;
  containerDiv: HTMLDivElement;

  constructor(
    position: google.maps.LatLng,
    price: number,
    id: number,
    selectedId: number,
    onClick: () => void,
    isExact?: boolean,
    isDisabled?: boolean,
  ) {
    super();
    this.position = position;

    this.containerDiv = document.createElement('div');
    this.containerDiv.classList.add('price-marker');
    this.containerDiv.innerText = `$${price}`;
    this.containerDiv.style.background = `url(${MapMarker})`;
    this.containerDiv.setAttribute('data-item-id', `${id}`);

    if (price) {
      if (id === selectedId || isExact) {
        this.containerDiv.style.zIndex = topZIndex;
        this.containerDiv.style.background = `url(${MapMarkerSelected})`;
      }
    } else {
      this.containerDiv.innerText = '';
      this.containerDiv.style.width = noPriceIconWidth;
      this.containerDiv.style.background = `url(${MapHomeMarker})`;

      if (id === selectedId || isExact) {
        this.containerDiv.style.zIndex = topZIndex;
        this.containerDiv.style.background = `url(${MapHomeSelectedMarker})`;
      }
    }

    if (isDisabled) {
      this.containerDiv.style.zIndex = topZIndex;
      this.containerDiv.style.background = `url(${MapHomeDisabledMarker})`;
    }

    if (isDisabled || isExact) {
      this.containerDiv.setAttribute('data-item-not-change', `${id}`);
    }

    this.containerDiv.addEventListener('click', onClick);
    PriceMarker.preventMapHitsAndGesturesFrom(this.containerDiv);
  }

  onAdd(): void {
    this.getPanes().floatPane.appendChild(this.containerDiv);
  }

  onRemove(): void {
    if (this.containerDiv.parentElement) {
      this.containerDiv.parentElement.removeChild(this.containerDiv);
    }
  }

  draw(): void {
    const divPosition = this.getProjection().fromLatLngToDivPixel(this.position);

    // Hide the popup when it is far out of view.
    const display =
      Math.abs(divPosition.x) < MAX_SHOW_AREA && Math.abs(divPosition.y) < MAX_SHOW_AREA
        ? 'block'
        : 'none';

    if (display === 'block') {
      this.containerDiv.style.left = divPosition.x - this.containerDiv.offsetWidth / two + 'px';
      this.containerDiv.style.top = divPosition.y - this.containerDiv.offsetHeight + 'px';
    }

    if (this.containerDiv.style.display !== display) {
      this.containerDiv.style.display = display;
    }
  }
}

const markers: (google.maps.Marker | PriceMarker)[] = [];

export class Map {
  static initializeMap(
    element: HTMLElement,
    point: google.maps.LatLngLiteral,
    options: google.maps.MapOptions = {},
  ): google.maps.Map {
    return new google.maps.Map(element, {
      center: point,
      zoom: DEFAULT_MAP_ZOOM,
      ...options,
    });
  }

  static addMarkers(
    map: google.maps.Map,
    locations: {
      location: IGeoLocation;
      id: number;
      pricePerNight?: number;
      isExact?: boolean;
      isDisabled?: boolean;
    }[],
    icon: string,
    selectedId = zero,
    onClick: (id: number) => void = undefined,
  ): void {
    locations.forEach(
      ({ location, id, pricePerNight = zero, isExact = false, isDisabled = false }) => {
        let marker: google.maps.Marker | PriceMarker;

        if (icon) {
          marker = new google.maps.Marker({
            position: Map.getGoogleLocation(location),
            map,
            icon,
          });
        } else {
          marker = new PriceMarker(
            new google.maps.LatLng(Map.getGoogleLocation(location)),
            pricePerNight,
            id,
            selectedId,
            () => {
              onClick(id);
            },
            isExact,
            isDisabled,
          );
          marker.setMap(map);
        }

        markers.push(marker);
      },
    );
  }

  static removeAllMarkers(): void {
    markers.forEach((marker) => {
      marker.setMap(null);
    });
  }

  static getGoogleLocation(location: IGeoLocation): google.maps.LatLngLiteral {
    return {
      lat: location.latitude,
      lng: location.longitude,
    };
  }
}
